import { compose, filter, map } from 'ramda';
import {
    paramContent,
    paramContentType,
    paramHeaderNamePath,
    paramHeaderValuePath,
    paramHeadersDsName,
    paramHeadersPath,
    paramMethod,
    paramPassword,
    paramPath,
    paramTimeoutms,
    paramUsername,
} from '../constants/parameterNames';
import {
    typeDataSource,
    typeNumber,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import { failure } from '../helpers/commandResult';
import {
    getIntParamValue,
    getParamValue,
    hasParam,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNodeSet } from '../helpers/qRules.node.helpers';

const METHOD_GET = 'GET';
const METHOD_POST = 'POST';
const METHOD_PUT = 'PUT';
const METHOD_DELETE = 'DELETE';

const DEFAULT_CONTENT_TYPE = 'text/plain';

const description = 'Carries out a network request and provides the result';

const requiredParameters = {
    [paramPath]: {
        description: 'Path where the request should be sent (URL, etc.)',
    },
};

const optionalParameters = {
    [paramUsername]: {
        description: 'Username to use for authentication',
    },
    [paramPassword]: {
        description: 'Password to use for authentication',
    },
    [paramMethod]: {
        // For backward compatibility with the behavior of the bug in case 47939,
        // determine default value based on whether or not the content parameter is specified.
        description: `HTTP Method to use. Defaults to ${METHOD_POST} if /${paramContent} is specified and ${METHOD_GET} if not.`,
        type: typeOption,
        options: [
            {
                name: METHOD_GET,
                value: METHOD_GET,
            },
            {
                name: METHOD_POST,
                value: METHOD_POST,
            },
            {
                name: METHOD_PUT,
                value: METHOD_PUT,
            },
            {
                name: METHOD_DELETE,
                value: METHOD_DELETE,
            },
        ],
    },
    [paramContentType]: {
        description: `Content type of content being sent. Default is ${DEFAULT_CONTENT_TYPE}. Only relevant when /content is specified.`,
    },
    [paramContent]: {
        description: 'Content to send in the request ',
    },
    [paramHeadersDsName]: {
        description: 'Data source XPath for header',
        type: typeDataSource,
    },
    [paramHeadersPath]: {
        description: 'Repeated section XPath for header',
        type: typeXPath,
        dsParentName: paramHeadersDsName,
    },
    [paramHeaderNamePath]: {
        description: 'Name XPath for header',
        type: typeXPath,
        dsParentName: paramHeadersDsName,
    },
    [paramHeaderValuePath]: {
        description: 'Value XPath for header',
        type: typeXPath,
        dsParentName: paramHeadersDsName,
    },
    [paramTimeoutms]: {
        description:
            'Timeout in milliseconds (terminate the request if it takes longer than this amount of time to complete)',
        type: typeNumber,
    },
};

const invalidHeaderNameXPathMsg = `/${paramHeaderNamePath} must be specified if ${paramHeadersPath} is specified.`;
const invalidHeaderValueXPathMsg = `/${paramHeaderValuePath} must be specified if ${paramHeadersPath} is specified.`;
const invalidHeaderDataSourceMsg = `The data source specified for /${paramHeadersDsName} could not be found.`;

// DataSourceNode -> Header
const headerFromNode = (headerNamePath, headerValuePath) => (node) => ({
    Key: node.evaluateString(headerNamePath),
    Value: node.evaluateString(headerValuePath),
});

// Header -> Boolean
const isValidHeader = ({ Key, Value }) => Key && Value;

// (String, String, [DataSourceNode]) -> [Header]
const getHeadersFromNodes = (headerNamePath, headerValuePath, nodes) =>
    compose(
        filter(isValidHeader),
        map(headerFromNode(headerNamePath, headerValuePath)),
    )(nodes);

// (String, String, String, String, DataSourceCollection) -> [Header]
const getHeadersInner = (
    headersPath,
    headerNamePath,
    headerValuePath,
    headersDsName,
    dataSources,
) => {
    if (!headersPath) {
        // not using headers
        return [];
    }
    if (!headerNamePath) {
        throw new Error(invalidHeaderNameXPathMsg);
    }
    if (!headerValuePath) {
        throw new Error(invalidHeaderValueXPathMsg);
    }

    const headerDataSource = getDataSource(dataSources, headersDsName);

    if (!headerDataSource) {
        throw new Error(invalidHeaderDataSourceMsg);
    }

    return getHeadersFromNodes(
        headerNamePath,
        headerValuePath,
        getValidNodeSet(headerDataSource, headersPath),
    );
};

// (qRulesParameters, DataSourceCollection) -> [Header]
const getHeaders = (parameters, dataSources) =>
    getHeadersInner(
        getParamValue(parameters, paramHeadersPath),
        getParamValue(parameters, paramHeaderNamePath),
        getParamValue(parameters, paramHeaderValuePath),
        getParamValue(parameters, paramHeadersDsName, ''),
        dataSources,
    );

async function executeAsync({ api, dataSources, parameters }) {
    const contentSpecified = hasParam(parameters, paramContent);

    const path = getParamValue(parameters, paramPath);
    const userName = getParamValue(parameters, paramUsername);
    const password = getParamValue(parameters, paramPassword);
    const method = getParamValue(
        parameters,
        paramMethod,
        contentSpecified ? METHOD_POST : METHOD_GET,
    );
    const contentType = getParamValue(
        parameters,
        paramContentType,
        DEFAULT_CONTENT_TYPE,
    );
    const content = getParamValue(parameters, paramContent, '');
    const timeoutms = getIntParamValue(parameters, paramTimeoutms, null);

    const headers = getHeaders(parameters, dataSources);

    const request = {
        Content: content,
        ContentType: contentType,
        Method: method,
        Password: password,
        url: path,
        Username: userName,
        headers,
        timeoutms,
    };

    const result = await api.callCrossDomainRequestAsync(request);

    if (result.success) {
        return result.resultBody;
    }

    return failure(result.message, result.resultBody);
}

const MakeRequest = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default MakeRequest;
